import { Form, Row, FormInstance, Col, Input } from 'antd';
import { useIntl } from 'react-intl';
import { FloatingFormItem, InputDecimal } from '@/components/_common';

import { useDefaultEmptyFieldError } from '@/hooks';

import { Purchase } from '@/core/domain/purchase/Purchase.types';
import {
  PurchaseItem,
  PurchaseItemsFormValues,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { RequestPurchases } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';
import { quantityFormatter } from '@/utils/number';

interface Props {
  form: FormInstance<PurchaseItemsFormValues>;
  purchase?: Purchase;
  requestPurchase?: RequestPurchases;
  purchaseItem?: PurchaseItem;
}

const defaultEmptyFieldError = useDefaultEmptyFieldError();

function GeneralTaskForm({
  form,
  purchase,
  requestPurchase,
  purchaseItem,
}: Props) {
  const intl = useIntl();

  const handleValuesChanged = (
    fieldsChanged: PurchaseItemsFormValues,
    fieldValues: PurchaseItemsFormValues,
  ) => {
    if (fieldsChanged.quantity || fieldsChanged.unitaryValue) {
      form.resetFields(['discount']);
    }

    form.setFieldsValue({
      totalAmount:
        (fieldValues.quantity ?? 0) * (fieldValues.unitaryValue ?? 0) -
        (fieldValues.discount ?? 0),
    });
  };

  const quantity = Form.useWatch(['quantity'], form);

  const unitaryValue = Form.useWatch(['unitaryValue'], form);

  return (
    <>
      <Form layout="vertical" form={form} onValuesChange={handleValuesChanged}>
        <Row gutter={[16, 9]}>
          <Col span={24} lg={14}>
            <FloatingFormItem
              name="name"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.description',
              })}
              rules={[defaultEmptyFieldError]}
            >
              <Input disabled={purchase?.boschMigration} />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="unitMeasurement"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.unit',
              })}
              rules={[defaultEmptyFieldError]}
              initialValue={'hr'}
            >
              <Input maxLength={5} disabled={purchase?.boschMigration} />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="quantity"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.quantity',
              })}
              initialValue={0}
              rules={
                requestPurchase && quantity >= (purchaseItem?.quantity ?? 0)
                  ? [
                      defaultEmptyFieldError,
                      {
                        type: 'number',
                        min: 0.01,
                        message: 'Infome uma quantidade maior que 0!',
                      },
                      {
                        type: 'number',
                        max:
                          (requestPurchase?.requestedQuantity ?? 0) -
                          ((requestPurchase?.quantityPurchased ?? 0) +
                            (requestPurchase.quantitySentPurchased ?? 0) -
                            (purchaseItem?.quantity ?? 0)),
                        message: `O máximo que poderá adicionar é ${quantityFormatter(
                          (requestPurchase?.requestedQuantity ?? 0) -
                            ((requestPurchase?.quantityPurchased ?? 0) +
                              (requestPurchase.quantitySentPurchased ?? 0) -
                              (purchaseItem?.quantity ?? 0)),
                        )}`,
                      },
                    ]
                  : [
                      defaultEmptyFieldError,
                      {
                        type: 'number',
                        min: 0.01,
                        message: 'Infome uma quantidade maior que 0!',
                      },
                    ]
              }
            >
              <InputDecimal min={0} disabled={purchase?.boschMigration} />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="unitaryValue"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.unitPrice',
              })}
              initialValue={0}
              rules={[defaultEmptyFieldError]}
            >
              <InputDecimal
                fractions={4}
                min={0}
                disabled={purchase?.boschMigration}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="discount"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.discountValue',
              })}
            >
              <InputDecimal
                min={0}
                max={(quantity ?? 0) * (unitaryValue ?? 0)}
                disabled={purchase?.boschMigration}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4}>
            <FloatingFormItem
              name="totalAmount"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.amount',
              })}
              initialValue={0}
            >
              <InputDecimal disabled />
            </FloatingFormItem>
          </Col>

          {purchase?.individuallyExpenses && (
            <Col span={24} lg={4}>
              <FloatingFormItem
                name={['purchaseItemTaxes', 'expenseValue']}
                label={intl.formatMessage({
                  id: 'purchases.details.itemsPurchase.create.expenses',
                })}
                initialValue={0}
              >
                <InputDecimal disabled={purchase?.boschMigration} />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} lg={14}>
            <FloatingFormItem
              name="itemNumber"
              label={intl.formatMessage({
                id: 'purchases.details.itemsPurchase.create.itemNumber',
              })}
            >
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default GeneralTaskForm;
